/**
 * Dashboard `?debug=1` panel — a thin wrapper over the shared
 * `@authe/ui-core/debug` `<DebugPanel>`. It injects the dashboard-specific rows
 * (env, the identity/tenancy claims, the deployment the client is pinned to, the
 * resolved Auth0 target) and leans on the shared panel for every generic
 * affordance: copyable ids, the scopes list, the raw-JWT reveal, sign-out (incl.
 * federated) and clear-cache — so that diagnostics logic is maintained once
 * across the fleet.
 *
 * This is also where the dashboard's SUPPORT surface lives, and two things moved
 * here from user Settings because they were support surfaces wearing a settings
 * costume:
 *
 *  - **Deployment** — deploy env, API URL, continent, region, backup region,
 *    latest-release routing (testers only) and the claims namespace. None of it
 *    is actionable by the person reading it; all of it is the first thing asked
 *    on a ticket. It sits
 *    in the rows above, beside the ids it is only useful next to.
 *  - **Transport** — the REST/GraphQL per-operation overrides (see
 *    `TransportDebugSection`).
 *
 * The token's own `feature_flags` claim is rendered as a read-only block: it is
 * a fact about the tenant, not a switch, and is deliberately separate from the
 * shared panel's flag TOGGLES (local overrides), which are a different layer and
 * were previously easy to confuse with it.
 *
 * An impersonated session is called out first: who is really acting, the
 * session id to quote in the audit trail, its access level and expiry, and which
 * issuer minted the token (a platform-issued session has no Auth0 client behind
 * it, so the client/audience/domain rows describe the app, not this token).
 *
 * Mounted inside the authenticated shell (below `<RequireSession>` +
 * `SessionScopedProviders`), so `useSession`/`useFeatureFlags` are available.
 */
import * as React from "react";
import { logoutDestination } from "@/app/logout-destination";
import { DebugPanel as SharedDebugPanel } from "@authe/ui-core/debug";
import { useSession } from "@authe/ui-core/session";
import { useFeatureFlags } from "@authe/ui-core/flags";
import { FEATURE_FLAGS } from "@authe/fe-commons/features";
import { useFeatureFlagSources } from "@/app/tenancyFeatureFlags";
import { summariseBlueRouting } from "@authe/fe-commons/blue-green";
import { readQaOverride } from "@authe/fe-commons/session";
import { Badge, cn } from "@authe/ui-core";
import { decodeJwtPayload, getImpersonationState } from "@authe/fe-commons/auth";
import type { AutheClaims } from "@authe/fe-commons/auth";
import {
  AWS_REGION_ALIASES,
  CONTINENT_SHORT_LABEL,
  continentForRegionOrAlias,
  convertRegionToBackupRegion,
  getRegionInfo,
} from "@authe/fe-commons/region";
import { appEnv, appConfig, auth0Config } from "@/config/env";

// Lazy on purpose. `React.lazy` only imports when the component RENDERS, and the
// shared panel returns null before it renders any section — so with debug off
// the operator tooling (`@authe/streaming/debug`, `@authe/ui-core/stream`) and
// the transport switchboard's `@/app/graphql` dependency are chunks that are
// never fetched, which is what their subpaths exist to guarantee.
const StreamingDebugSection = React.lazy(() => import("./StreamingDebugSection"));
const TransportDebugSection = React.lazy(() => import("./TransportDebugSection"));

/**
 * Friendly `alias · City` for a region alias (`pdx`) or full AWS region
 * (`us-west-2`); returns the raw value if the region isn't in the registry.
 */
function describeRegion(aliasOrRegion: string | null | undefined): string | null {
  if (!aliasOrRegion) return null;
  const full = AWS_REGION_ALIASES[aliasOrRegion] ?? aliasOrRegion;
  try {
    const info = getRegionInfo(full);
    return `${info.alias} · ${info.fullName}`;
  } catch {
    return aliasOrRegion;
  }
}

/** Paired failover region for a primary alias/region, or null when unknown. */
function backupRegionFor(aliasOrRegion: string | null | undefined): string | null {
  if (!aliasOrRegion) return null;
  const full = AWS_REGION_ALIASES[aliasOrRegion] ?? aliasOrRegion;
  try {
    return convertRegionToBackupRegion(full);
  } catch {
    return null;
  }
}

/**
 * Every declared customer flag, as THIS SESSION resolved it, and which layer
 * decided.
 *
 * It used to render the token's `feature_flags` claim. Nothing writes that claim
 * — the customer flags reach the app through management-api `GET /features` and
 * land on the store's CLAIM LAYER (see `app/DashboardFeatureFlags`) — so the one
 * place support looks to answer "is this on for them?" said "No flags set for
 * this tenant" for every tenant, including one with `ask_ai` switched on for
 * their user. An empty answer that looks like a fact is worse than no panel.
 *
 * The layer is the other half of the answer: `override` is this browser's own
 * `?debug=1` switch and tells support the tenancy's value is NOT what they are
 * looking at, while `claim` is what the admin portal set and `default` is what
 * the code ships.
 */
const SOURCE_NOTE: Record<string, string> = {
  override: "this browser's own ?debug switch — NOT the tenancy's value",
  policy: "a managed workspace policy",
  customer: "set for the customer, in the admin portal",
  org: "set for the organization, in the admin portal",
  tenant: "set for the tenant, in the admin portal",
  user: "set for this user, in the admin portal",
  default: "the code's default — nobody has set it",
};

function ResolvedFlags(): React.ReactElement {
  const flags = useFeatureFlags();
  // The rung the BACKEND resolved it at. The store's own layer says "claim" for
  // every declared flag (defaults are filled into that layer too), which cannot
  // tell "an operator switched this on" from "nobody has touched it".
  const sources = useFeatureFlagSources();
  if (FEATURE_FLAGS.length === 0) {
    return <p className="text-[11px] text-muted-foreground">No customer feature flags are declared.</p>;
  }
  return (
    <ul className="space-y-1">
      {FEATURE_FLAGS.map((flag) => {
        const on = flags.isEnabled(flag.name);
        // A local override wins over everything and must SAY so — support
        // reading "user" off a browser that is lying to itself is the whole
        // reason this line exists.
        const overridden = flags.layerOf(flag.name) === "override";
        const source = overridden ? "override" : (sources[flag.name] ?? "default");
        return (
          <li key={flag.name} className="flex items-center justify-between gap-2 text-[11px]">
            <span className="min-w-0 truncate font-mono text-muted-foreground" title={flag.description}>
              {flag.name}
            </span>
            <span className="flex shrink-0 items-center gap-1">
              <span
                className={cn("text-[9px]", overridden ? "font-medium text-risk-medium" : "text-muted-foreground")}
                title={SOURCE_NOTE[source]}
              >
                {source}
              </span>
              <Badge variant={on ? "default" : "secondary"} className="shrink-0 px-1 text-[9px]">
                {on ? "on" : "off"}
              </Badge>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function DebugPanel(): React.ReactElement | null {
  const { session, logout, getAccessToken } = useSession();
  const featureFlags = useFeatureFlags();
  const claims = session?.claims;
  // Blue-only: a session on the live release gets no row (the live colour is
  // never named), a tester sees what runs on the latest release and any `?qa=`.
  const blueRelease = summariseBlueRouting(featureFlags.routingConfig());
  const qaOverride = readQaOverride();

  // Region/continent: prefer the per-tenant `region_config` claim (what the API
  // client actually routes on), falling back to the build-time
  // `appConfig.region` and the static pairing in `@authe/fe-commons/region`.
  const regionConfig = claims?.region_config ?? null;
  const primaryRegion =
    describeRegion(regionConfig?.region_alias ?? regionConfig?.region ?? appConfig.region) ??
    appConfig.region;
  const backupRegion = describeRegion(
    regionConfig?.backup_region_alias ??
      regionConfig?.backup_region ??
      backupRegionFor(appConfig.region),
  );
  const continent = (() => {
    if (claims?.continent) return String(claims.continent).toUpperCase();
    const c = continentForRegionOrAlias(appConfig.region);
    return c ? CONTINENT_SHORT_LABEL[c] : "—";
  })();

  const impersonation = getImpersonationState(claims as AutheClaims | undefined, {
    targetSub: session?.user.id ?? null,
  });
  const tokenIssuer = decodeJwtPayload(session?.accessToken)?.iss;
  const impersonationRows = impersonation.active
    ? [
        {
          label: "session",
          value: `IMPERSONATED · ${impersonation.accessLevel === "read_write" ? "read/write" : "read-only"}${
            impersonation.expired ? " · EXPIRED" : ""
          }`,
        },
        { label: "acting as", value: session?.user.email ?? impersonation.targetSub ?? "—", copyable: true },
        { label: "operator (act)", value: impersonation.actorSub ?? "—", copyable: true },
        { label: "impersonation id", value: impersonation.sessionId ?? "—", copyable: true },
        { label: "expires", value: impersonation.expiresAt ?? "—" },
        { label: "reason", value: impersonation.reason ?? "—" },
      ]
    : [];

  return (
    <SharedDebugPanel
      title={impersonation.active ? "Dashboard debug — IMPERSONATED SESSION" : "Dashboard debug"}
      rows={[
        ...impersonationRows,
        { label: "env", value: appEnv.env },
        { label: "api", value: appConfig.apiUrl, copyable: true },
        { label: "org", value: claims?.org_id ?? "—", copyable: true },
        { label: "tenant", value: claims?.tenant_id ?? "—", copyable: true },
        { label: "user id", value: claims?.user_id ?? session?.user.id ?? "—", copyable: true },
        { label: "email", value: session?.user.email ?? "—", copyable: true },
        { label: "continent", value: continent },
        { label: "region", value: primaryRegion },
        { label: "backup region", value: backupRegion ?? "—" },
        ...(blueRelease ? [{ label: "latest release", value: blueRelease }] : []),
        ...(qaOverride ? [{ label: "qa override", value: `?qa=${qaOverride}`, copyable: true }] : []),
        { label: "application", value: claims?.application ?? "—" },
        { label: "claims ns", value: appEnv.claimsNamespace, copyable: true },
        { label: "client id", value: auth0Config.clientId, copyable: true },
        { label: "audience", value: auth0Config.audience ?? "—", copyable: true },
        { label: "domain", value: auth0Config.domain },
        { label: "token issuer", value: typeof tokenIssuer === "string" ? tokenIssuer : "—", copyable: true },
        ...(impersonation.active ? [] : [{ label: "session", value: session ? "authenticated" : "—" }]),
      ]}
      sections={[
        ...(impersonation.active
          ? [
              {
                title: "Impersonation",
                content: (
                  <p className="text-xs text-muted-foreground">
                    The scopes listed above are the target&apos;s role grants as the app reads
                    them. The API reduces them to{" "}
                    {impersonation.accessLevel === "read_write" ? "read/write" : "read-only"} on
                    every request, so a write the UI offers can still be refused. Signing out
                    ends only this tab&apos;s session; the federated sign-out is hidden because it
                    would also end the operator&apos;s own Auth0 session.
                  </p>
                ),
              },
            ]
          : []),
        // Live streaming: connection + leadership + what is subscribed + a
        // wildcard event log with an ad-hoc channel picker. Everything a shared
        // multi-tab socket can go wrong in was previously answerable only from
        // `window.__autheStreaming` in a console.
        {
          title: "Streaming",
          content: (
            <React.Suspense
              fallback={<p className="text-xs text-muted-foreground">Loading…</p>}
            >
              <StreamingDebugSection />
            </React.Suspense>
          ),
        },
        {
          title: "APIs (REST vs GraphQL)",
          content: (
            <React.Suspense
              fallback={<p className="text-xs text-muted-foreground">Loading…</p>}
            >
              <TransportDebugSection />
            </React.Suspense>
          ),
        },
        {
          title: "Customer feature flags",
          content: <ResolvedFlags />,
        },
      ]}
      scopes={claims?.scopes ?? []}
      getAccessToken={getAccessToken}
      // The user-facing sign-out (ProfileMenu) deliberately skips `federated`;
      // this is a dev-gated (`?debug=1`) tool, so it also offers a federated
      // sign-out to fully reset the upstream IdP session when testing accounts.
      onSignOut={({ federated }) =>
        void logout({ returnTo: logoutDestination(), federated })
      }
      // Federated sign-out ends the Auth0 SSO session, which under impersonation
      // is the OPERATOR's — it would sign them out of the admin portal too.
      federatedSignOut={!impersonation.active}
      // No `flagNames`: it rendered exactly the `graphql.*` set the transport
      // section above now owns, as unlabelled switches. Two controls for one
      // flag, one of them saying nothing about what it does.
    />
  );
}
